import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Target, Palette, Shield, BookOpen, Lightbulb, Rocket, ChevronDown, ChevronUp, MessageSquare, Play, Save, Sparkles } from 'lucide-react';
import ReactDOM from 'react-dom';
import { useAgent } from '../contexts/AgentContext';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppInput } from '../components/base/AppInput';
import { AppTextarea } from '../components/base/AppTextarea';
import { AppSwitch } from '../components/base/AppSwitch';
import { AppBadge } from '../components/base/AppBadge';
import { Toast } from '../components/Toast';
import * as mockApi from '../services/mockApi';
import { mockAgentConfigs, mockKnowledgeSourcesByAgent, mockToolsByAgent } from '../data/mockData';
import { AgentParametriTab } from '../components/tabs/AgentParametriTab';
import { AgentIstruzioniTab } from '../components/tabs/AgentIstruzioniTab';
export function ServizioDetailPage() {
  const {
    agentId
  } = useParams<{
    agentId: string;
  }>();
  const navigate = useNavigate();
  const {
    setActiveAgentId
  } = useAgent();
  const [currentAgent, setCurrentAgent] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Form state
  const [objective, setObjective] = useState('');
  const [stylePreset, setStylePreset] = useState('balanced');
  const [creativityLevel, setCreativityLevel] = useState(3);
  const [responseLength, setResponseLength] = useState('medium');
  const [constraints, setConstraints] = useState('');
  const [selectedKnowledge, setSelectedKnowledge] = useState<string[]>([]);
  const [enabledTools, setEnabledTools] = useState<string[]>([]);
  const [isPublished, setIsPublished] = useState(true);
  useEffect(() => {
    if (agentId) {
      setActiveAgentId(agentId);
      loadAgent();
    }
  }, [agentId, setActiveAgentId]);
  const loadAgent = async () => {
    if (!agentId) return;
    try {
      const agent = await mockApi.getAgent(agentId);
      setCurrentAgent(agent);
      // Load config
      const config = mockAgentConfigs[agentId] || mockAgentConfigs['default'];
      setCreativityLevel(config.creativityLevel);
      setObjective(agent.shortDescription || '');
      // Load knowledge
      const knowledge = mockKnowledgeSourcesByAgent[agentId] || [];
      setSelectedKnowledge(knowledge.filter(k => k.status === 'Attivo').map(k => k.id));
      // Load tools
      const tools = mockToolsByAgent[agentId] || mockToolsByAgent['default'];
      setEnabledTools(tools.tools.filter(t => t.enabled).map(t => t.id));
    } catch (error) {
      console.error('Failed to load agent', error);
    }
  };
  const handleSave = async () => {
    if (!agentId || !hasChanges) return;
    setIsSaving(true);
    try {
      await mockApi.updateAgentConfig(agentId, {
        lastModified: new Date().toISOString()
      });
      setHasChanges(false);
      setToast({
        message: 'Servizio salvato',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nel salvataggio',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  // Render Save button in page action slot
  useEffect(() => {
    const actionSlot = document.getElementById('page-action-slot');
    if (actionSlot) {
      const saveButton = <AppButton variant={hasChanges ? 'primary' : 'secondary'} size="sm" disabled={!hasChanges || isSaving} onClick={handleSave} data-id="element-1856">
          <Save className="w-4 h-4 mr-2" data-id="element-1857" />
          {isSaving ? 'Salvataggio...' : 'Salva'}
        </AppButton>;
      ReactDOM.render(saveButton, actionSlot);
    }
    return () => {
      const actionSlot = document.getElementById('page-action-slot');
      if (actionSlot) {
        ReactDOM.unmountComponentAtNode(actionSlot);
      }
    };
  }, [hasChanges, isSaving]);
  if (!currentAgent) {
    return <div className="flex items-center justify-center py-20" data-id="element-1858">
        <p className="text-gray-500 dark:text-gray-400" data-id="element-1859">Caricamento...</p>
      </div>;
  }
  const stylePresets = [{
    id: 'professional',
    label: 'Professionale',
    description: 'Formale e preciso'
  }, {
    id: 'friendly',
    label: 'Amichevole',
    description: 'Cordiale e conversazionale'
  }, {
    id: 'balanced',
    label: 'Bilanciato',
    description: 'Equilibrato e versatile'
  }, {
    id: 'concise',
    label: 'Conciso',
    description: 'Breve e diretto'
  }];
  const responseLengthOptions = [{
    id: 'short',
    label: 'Breve',
    description: '1-2 paragrafi'
  }, {
    id: 'medium',
    label: 'Media',
    description: '3-5 paragrafi'
  }, {
    id: 'long',
    label: 'Lunga',
    description: '6+ paragrafi'
  }];
  const availableKnowledge = mockKnowledgeSourcesByAgent[agentId] || [];
  const availableTools = mockToolsByAgent[agentId]?.tools || [];
  return <div className="pb-12" data-id="element-1860">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1861" />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1862">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1863">
          {/* 1. Obiettivo */}
          <AppCard data-id="element-1864">
            <div className="flex items-start mb-4" data-id="element-1865">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg mr-3" data-id="element-1866">
                <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1867" />
              </div>
              <div className="flex-1" data-id="element-1868">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1" data-id="element-1869">
                  Obiettivo del servizio
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1870">
                  Descrivi cosa deve fare questo servizio
                </p>
              </div>
            </div>
            <AppTextarea value={objective} onChange={e => {
            setObjective(e.target.value);
            setHasChanges(true);
          }} placeholder="Es: Aiutare gli studenti a comprendere concetti complessi con spiegazioni semplici e esempi pratici" rows={3} data-id="element-1871" />
          </AppCard>

          {/* 2. Stile */}
          <AppCard data-id="element-1872">
            <div className="flex items-start mb-4" data-id="element-1873">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg mr-3" data-id="element-1874">
                <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-1875" />
              </div>
              <div className="flex-1" data-id="element-1876">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1" data-id="element-1877">
                  Stile di comunicazione
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1878">
                  Personalizza tono e lunghezza delle risposte
                </p>
              </div>
            </div>

            {/* Style Preset Selector */}
            <div className="mb-6" data-id="element-1879">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-1880">
                Tono
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-1881">
                {stylePresets.map(preset => <button key={preset.id} onClick={() => {
                setStylePreset(preset.id);
                setHasChanges(true);
              }} className={`p-3 rounded-xl border-2 transition-all text-left ${stylePreset === preset.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-dark-subtle hover:border-gray-300 dark:hover:border-dark-medium'}`} data-id="element-1882">
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100" data-id="element-1883">
                      {preset.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1884">
                      {preset.description}
                    </p>
                  </button>)}
              </div>
            </div>

            {/* Creativity Level */}
            <div className="mb-6" data-id="element-1885">
              <div className="flex items-center justify-between mb-3" data-id="element-1886">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1887">
                  Livello di creatività
                </label>
                <span className="text-sm text-blue-600 dark:text-blue-400 font-medium" data-id="element-1888">
                  {creativityLevel}/5
                </span>
              </div>
              <input type="range" min="1" max="5" step="1" value={creativityLevel} onChange={e => {
              setCreativityLevel(parseInt(e.target.value));
              setHasChanges(true);
            }} className="w-full h-2 bg-gray-200 dark:bg-dark-subtle rounded-lg appearance-none cursor-pointer" data-id="element-1889" />
              <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-1890">
                <span data-id="element-1891">Preciso</span>
                <span data-id="element-1892">Creativo</span>
              </div>
            </div>

            {/* Response Length */}
            <div data-id="element-1893">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-1894">
                Lunghezza risposte
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-1895">
                {responseLengthOptions.map(option => <button key={option.id} onClick={() => {
                setResponseLength(option.id);
                setHasChanges(true);
              }} className={`p-3 rounded-xl border-2 transition-all text-center ${responseLength === option.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-dark-subtle hover:border-gray-300 dark:hover:border-dark-medium'}`} data-id="element-1896">
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100" data-id="element-1897">
                      {option.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1898">
                      {option.description}
                    </p>
                  </button>)}
              </div>
            </div>
          </AppCard>

          {/* 3. Limiti */}
          <AppCard data-id="element-1899">
            <div className="flex items-start mb-4" data-id="element-1900">
              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg mr-3" data-id="element-1901">
                <Shield className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-1902" />
              </div>
              <div className="flex-1" data-id="element-1903">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1" data-id="element-1904">
                  Limiti e regole
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1905">
                  Cosa NON deve fare il servizio
                </p>
              </div>
            </div>
            <AppTextarea value={constraints} onChange={e => {
            setConstraints(e.target.value);
            setHasChanges(true);
          }} placeholder="Es: Non fornire consigli medici, non condividere informazioni personali, non generare contenuti inappropriati" rows={3} data-id="element-1906" />
          </AppCard>

          {/* 4. Knowledge */}
          <AppCard data-id="element-1907">
            <div className="flex items-start mb-4" data-id="element-1908">
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg mr-3" data-id="element-1909">
                <BookOpen className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-1910" />
              </div>
              <div className="flex-1" data-id="element-1911">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1" data-id="element-1912">
                  Fonti di conoscenza
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1913">
                  Seleziona le fonti da cui il servizio può attingere
                </p>
              </div>
            </div>
            <div className="space-y-2" data-id="element-1914">
              {availableKnowledge.length === 0 ? <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center" data-id="element-1915">
                  Nessuna fonte disponibile
                </p> : availableKnowledge.map(source => <div key={source.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-dark-subtle hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors" data-id="element-1916">
                    <div className="flex-1" data-id="element-1917">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1918">
                        {source.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1919">
                        {source.documentsCount} documenti
                      </p>
                    </div>
                    <AppSwitch checked={selectedKnowledge.includes(source.id)} onChange={checked => {
                setSelectedKnowledge(checked ? [...selectedKnowledge, source.id] : selectedKnowledge.filter(id => id !== source.id));
                setHasChanges(true);
              }} data-id="element-1920" />
                  </div>)}
            </div>
          </AppCard>

          {/* 5. Funzionalità */}
          <AppCard data-id="element-1921">
            <div className="flex items-start mb-4" data-id="element-1922">
              <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg mr-3" data-id="element-1923">
                <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-1924" />
              </div>
              <div className="flex-1" data-id="element-1925">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1" data-id="element-1926">
                  Funzionalità abilitate
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1927">
                  Abilita capacità aggiuntive per il servizio
                </p>
              </div>
            </div>
            <div className="space-y-2" data-id="element-1928">
              {availableTools.length === 0 ? <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center" data-id="element-1929">
                  Nessuna funzionalità disponibile
                </p> : availableTools.map(tool => <div key={tool.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-dark-subtle hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors" data-id="element-1930">
                    <div className="flex-1" data-id="element-1931">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1932">
                        {tool.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1933">
                        {tool.description}
                      </p>
                    </div>
                    <AppSwitch checked={enabledTools.includes(tool.id)} onChange={checked => {
                setEnabledTools(checked ? [...enabledTools, tool.id] : enabledTools.filter(id => id !== tool.id));
                setHasChanges(true);
              }} data-id="element-1934" />
                  </div>)}
            </div>
          </AppCard>

          {/* 6. Pubblicazione */}
          <AppCard data-id="element-1935">
            <div className="flex items-start mb-4" data-id="element-1936">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg mr-3" data-id="element-1937">
                <Rocket className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1938" />
              </div>
              <div className="flex-1" data-id="element-1939">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1" data-id="element-1940">
                  Pubblicazione
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1941">
                  Controlla la disponibilità del servizio
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-subtle rounded-lg" data-id="element-1942">
              <div data-id="element-1943">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1944">
                  Servizio attivo
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1945">
                  {isPublished ? 'Disponibile agli utenti' : 'Non disponibile'}
                </p>
              </div>
              <AppSwitch checked={isPublished} onChange={checked => {
              setIsPublished(checked);
              setHasChanges(true);
            }} data-id="element-1946" />
            </div>
          </AppCard>

          {/* Advanced Accordion */}
          <AppCard className="border-2 border-dashed border-gray-300 dark:border-dark-medium" data-id="element-1947">
            <button onClick={() => setShowAdvanced(!showAdvanced)} className="w-full flex items-center justify-between" data-id="element-1948">
              <div className="flex items-center" data-id="element-1949">
                <Sparkles className="w-5 h-5 text-gray-400 mr-2" data-id="element-1950" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1951">
                  Impostazioni avanzate
                </span>
              </div>
              {showAdvanced ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-1952" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-1953" />}
            </button>

            {showAdvanced && <div className="mt-6 space-y-6 pt-6 border-t border-gray-200 dark:border-dark-subtle" data-id="element-1954">
                <div data-id="element-1955">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4" data-id="element-1956">
                    Parametri tecnici
                  </h4>
                  <AgentParametriTab onDataChange={() => setHasChanges(true)} data-id="element-1957" />
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-dark-subtle" data-id="element-1958">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4" data-id="element-1959">
                    Istruzioni comportamento
                  </h4>
                  <AgentIstruzioniTab data-id="element-1960" />
                </div>
              </div>}
          </AppCard>
        </div>

        {/* Right Column - Preview */}
        <div className="lg:col-span-1" data-id="element-1961">
          <div className="sticky top-8 space-y-4" data-id="element-1962">
            {/* Example Conversation Preview */}
            <AppCard data-id="element-1963">
              <div className="flex items-center justify-between mb-4" data-id="element-1964">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1965">
                  Anteprima conversazione
                </h3>
                <AppBadge variant="blue" data-id="element-1966">Esempio</AppBadge>
              </div>

              <div className="space-y-3" data-id="element-1967">
                {/* User Message */}
                <div className="flex justify-end" data-id="element-1968">
                  <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm px-4 py-2 max-w-[80%]" data-id="element-1969">
                    <p className="text-sm" data-id="element-1970">
                      {currentAgent.examplePrompt || 'Ciao, come funziona?'}
                    </p>
                  </div>
                </div>

                {/* Agent Response */}
                <div className="flex justify-start" data-id="element-1971">
                  <div className="bg-gray-100 dark:bg-dark-subtle text-gray-900 dark:text-gray-100 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[80%]" data-id="element-1972">
                    <p className="text-sm" data-id="element-1973">
                      {currentAgent.exampleResponse || 'Ciao! Sono qui per aiutarti. Come posso esserti utile?'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-subtle" data-id="element-1974">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" data-id="element-1975">
                  Questa è un'anteprima di come il servizio risponderà agli
                  utenti
                </p>
              </div>
            </AppCard>

            {/* Test CTA */}
            <AppButton variant="primary" fullWidth icon={<Play className="w-4 h-4" data-id="element-1977" />} data-id="element-1976">
              Prova come utente
            </AppButton>

            {/* Info Card */}
            <AppCard className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800" data-id="element-1978">
              <div className="flex items-start" data-id="element-1979">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" data-id="element-1980" />
                <div data-id="element-1981">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1" data-id="element-1982">
                    Suggerimento
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300" data-id="element-1983">
                    Modifica l'obiettivo e lo stile per vedere come cambiano le
                    risposte del servizio
                  </p>
                </div>
              </div>
            </AppCard>
          </div>
        </div>
      </div>
    </div>;
}